import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported, type Messaging } from 'firebase/messaging';
import { deviceTokenAPI, employeePortalAPI } from './api';

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const hasFirebaseConfig = Object.values(firebaseConfig).every(Boolean);

const initPushInternal = async (
  registerToken: (token: string) => Promise<unknown>,
  storageKey: string
): Promise<void> => {
  if (!hasFirebaseConfig) {
    console.warn('Firebase push notifications skipped: missing VITE_FIREBASE_* config.');
    return;
  }

  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    console.warn('Push notifications skipped: browser support is unavailable.');
    return;
  }

  const supported = await isSupported().catch(() => false);
  if (!supported) {
    console.warn('Firebase messaging is not supported in this browser.');
    return;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.warn('Push notification permission was not granted.');
    return;
  }

  const swParams = new URLSearchParams(firebaseConfig as Record<string, string>);
  const registration = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${swParams.toString()}`);
  app = app || initializeApp(firebaseConfig);
  messaging = messaging || getMessaging(app);

  const token = await getToken(messaging, { serviceWorkerRegistration: registration }).catch(error => {
    console.warn('Failed to get FCM token.', error);
    return null;
  });

  if (!token) return;

  localStorage.setItem(storageKey, token);
  await registerToken(token).catch(error => {
    console.warn('Failed to register FCM token with backend.', error);
  });

  if (!onMessageAttached) {
    onMessageAttached = true;
    onMessage(messaging, payload => {
      console.log('payload', payload);
      window.dispatchEvent(new CustomEvent('app:notification-received', { detail: payload }));
    });
  }
};

let onMessageAttached = false;

export const initPushNotifications = async (): Promise<void> => {
  try {
    await initPushInternal(
      (token) => deviceTokenAPI.registerToken({ token, platform: 'web' }),
      'fcm_token'
    );
  } catch (error) {
    console.warn('Firebase push notifications skipped after initialization failure.', error);
  }
};

export const initEmployeePushNotifications = async (): Promise<void> => {
  try {
    await initPushInternal(
      (token) => employeePortalAPI.registerDeviceToken({ token, platform: 'web' }),
      'employee_fcm_token'
    );
  } catch (error) {
    console.warn('Employee Firebase push notifications skipped after initialization failure.', error);
  }
};

const unregisterInternal = async (
  token: string | null,
  unregisterToken: (token: string) => Promise<unknown>,
  storageKey: string
): Promise<void> => {
  if (!token) return;

  await unregisterToken(token).catch(error => {
    console.warn('Failed to unregister FCM token with backend.', error);
  });
  localStorage.removeItem(storageKey);
};

export const unregisterPush = async (): Promise<void> => {
  await unregisterInternal(
    localStorage.getItem('fcm_token'),
    (token) => deviceTokenAPI.unregisterToken({ token }),
    'fcm_token'
  );
};

export const unregisterEmployeePush = async (): Promise<void> => {
  await unregisterInternal(
    localStorage.getItem('employee_fcm_token'),
    (token) => employeePortalAPI.unregisterDeviceToken({ token }),
    'employee_fcm_token'
  );
};
